import type { BrandSectionKey } from '@mg/shared';
import type { EditPageKey } from './paths';

/**
 * The Edit Brand Workspace pages and their sections (spec:283-288; web.md §6.1). One list for the side
 * menu (lane W3) and the route redirects (lane W4). A section's `id` is the anchor of its card on the
 * page (`<section id>`), the target of its side-menu entry.
 */

export interface EditPageSection {
  id: string;
  label: string;
}

export interface EditPageDefinition {
  key: EditPageKey;
  /** The side menu's entry. */
  label: string;
  /** Hidden from members, who are redirected away from it (E30). */
  adminOnly: boolean;
  /** Shown under the entry while the page is active. Audience cards are never entries (spec:281). */
  sections: readonly EditPageSection[];
}

/** The anchor of each Brand information card, by its shared section key. */
export const BRAND_SECTION_IDS: Readonly<Record<BrandSectionKey, string>> = {
  workspaceDetails: 'workspace-details',
  companyVoice: 'company-voice',
  brandIdentity: 'brand-identity',
  brandVoice: 'brand-voice',
  vocabulary: 'vocabulary',
  writingGuidelines: 'writing-guidelines',
  audiences: 'audiences',
  market: 'market',
};

export const EDIT_PAGES: readonly EditPageDefinition[] = [
  {
    key: 'brand',
    label: 'Brand information',
    adminOnly: false,
    sections: [
      { id: BRAND_SECTION_IDS.workspaceDetails, label: 'Workspace details' },
      { id: BRAND_SECTION_IDS.companyVoice, label: 'Company voice and identity' },
      { id: BRAND_SECTION_IDS.brandIdentity, label: 'Brand identity' },
      { id: BRAND_SECTION_IDS.brandVoice, label: 'Brand voice' },
      { id: BRAND_SECTION_IDS.vocabulary, label: 'Vocabulary' },
      { id: BRAND_SECTION_IDS.writingGuidelines, label: 'Writing guidelines' },
      { id: BRAND_SECTION_IDS.audiences, label: 'Audiences' },
      { id: BRAND_SECTION_IDS.market, label: 'Market' },
    ],
  },
  {
    key: 'assets',
    label: 'Brand assets and style',
    adminOnly: false,
    sections: [
      { id: 'color-schemes', label: 'Color schemes' },
      { id: 'fonts-typography', label: 'Fonts and typography' },
      { id: 'logos-visual-assets', label: 'Logos and visual assets' },
    ],
  },
  {
    key: 'workflow',
    label: 'Workflow',
    adminOnly: false,
    sections: [
      // `approvals` is also the test-mode deep link's anchor (features/testMode/testMode.ts).
      { id: 'approvals', label: 'Approvals' },
      { id: 'approval-role-assignment', label: 'Approval role assignment' },
      { id: 'image-generation', label: 'Image generation' },
      { id: 'design-templates', label: 'Design templates' },
    ],
  },
  { key: 'danger', label: 'Danger zone', adminOnly: true, sections: [] },
];

/** The definition of one page. */
export function editPage(key: EditPageKey): EditPageDefinition {
  const page = EDIT_PAGES.find((p) => p.key === key);
  if (!page) throw new Error(`Unknown Edit Brand Workspace page: ${key}`);
  return page;
}

/** The pages a reader sees in the side menu: members never see an admin-only page. */
export function visibleEditPages(canEdit: boolean): EditPageDefinition[] {
  return EDIT_PAGES.filter((page) => canEdit || !page.adminOnly);
}
